import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import FiveCrownsGame from '../components/FiveCrownsGame';
import Phase10Game from '../components/Phase10Game';
import { apiFetch } from '../utils/api';
import {
  cacheGame,
  readCachedGame,
  setPendingState,
  readPendingState,
  clearPendingState
} from '../utils/gameCache';

export default function Game() {
  const { activeGameId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [activeGame, setActiveGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [socket, setSocket] = useState(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [consecutiveSaveFailures, setConsecutiveSaveFailures] = useState(0);
  // Rendering from cache because the server couldn't be reached
  const [servedFromCache, setServedFromCache] = useState(false);

  // Falls back to the on-device cache when the server can't be reached, so an
  // outage mid-game shows the scorecard rather than a dead end.
  const fallBackToCache = useCallback(() => {
    const cached = readCachedGame(activeGameId);
    if (cached) {
      const pending = readPendingState(activeGameId);
      setActiveGame(pending ? { ...cached, game_state: pending } : cached);
      setServedFromCache(true);
      setLoadError(false);
      if (pending) setSaveError(true);
    } else {
      setLoadError(true);
    }
    setLoading(false);
  }, [activeGameId]);

  const fetchActiveGame = useCallback(async (retry = 0) => {
    try {
      const res = await apiFetch(`/api/games/active/${activeGameId}`);
      if (!res.ok) {
        // Retry up to 2 times before falling back
        if (retry < 2) {
          setTimeout(() => fetchActiveGame(retry + 1), 1500);
          return;
        }
        fallBackToCache();
        return;
      }
      const data = await res.json();

      // A pending local edit is newer than whatever the server just returned,
      // so don't let a successful fetch clobber unsaved scores.
      const pending = readPendingState(activeGameId);
      const game = pending ? { ...data, game_state: pending } : data;

      setActiveGame(game);
      setLoading(false);
      setLoadError(false);
      setServedFromCache(false);
    } catch (error) {
      console.error('Error fetching active game:', error);
      // Retry up to 2 times before falling back
      if (retry < 2) {
        setTimeout(() => fetchActiveGame(retry + 1), 1500);
        return;
      }
      fallBackToCache();
    }
  }, [activeGameId, fallBackToCache]);

  useEffect(() => {
    fetchActiveGame();

    // Connect to Socket.io for real-time updates
    const newSocket = io(window.API_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      newSocket.emit('user-online', { token });
      newSocket.emit('join-game', { gameCode: activeGameId });
    });

    newSocket.on('game-state', (state) => {
      setActiveGame(prev => prev ? { ...prev, game_state: state } : prev);
    });

    setSocket(newSocket);

    return () => {
      newSocket.emit('leave-game', { gameCode: activeGameId });
      newSocket.disconnect();
    };
    // Intentionally keyed on activeGameId/token only: re-running on every
    // fetchActiveGame identity change would reconnect the socket mid-game.
  }, [activeGameId, token]);

  // Keep the device copy current on every change, so a crash or refresh during
  // an outage still comes back to the right scorecard.
  useEffect(() => {
    if (activeGame) cacheGame(activeGameId, activeGame);
  }, [activeGame, activeGameId]);

  const pushState = useCallback(async (state) => {
    const res = await apiFetch(`/api/games/active/${activeGameId}/state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_state: state })
    });
    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    return res;
  }, [activeGameId]);

  const handleUpdateGameState = async (newState) => {
    // Record locally before attempting the network call. The device copy must
    // never lag what the player sees, or a failed save loses scores.
    setPendingState(activeGameId, newState);
    setActiveGame(prev => (prev ? { ...prev, game_state: newState } : prev));

    try {
      await pushState(newState);
      clearPendingState(activeGameId);
      setSaveError(false);
      setConsecutiveSaveFailures(0);
      setServedFromCache(false);

      // Broadcast to other players
      if (socket) {
        socket.emit('game-state', { gameCode: activeGameId, state: newState });
      }
    } catch (error) {
      console.error('Error updating game state:', error);
      setSaveError(true);
      setConsecutiveSaveFailures(prev => prev + 1);
    }
  };

  // Flush unsaved state in the background so play continues during an outage
  // and scores land on their own once the backend recovers, without anyone
  // having to notice a banner and press a button.
  useEffect(() => {
    if (!saveError) return undefined;

    const timer = setInterval(async () => {
      const pending = readPendingState(activeGameId);
      if (!pending) {
        setSaveError(false);
        return;
      }
      try {
        await pushState(pending);
        clearPendingState(activeGameId);
        setSaveError(false);
        setConsecutiveSaveFailures(0);
        setServedFromCache(false);
        if (socket) {
          socket.emit('game-state', { gameCode: activeGameId, state: pending });
        }
      } catch (e) {
        // Still unreachable; the next tick tries again.
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [saveError, activeGameId, socket, pushState]);

  const handleEndGame = async (scores, keepStats = true) => {
    try {
      const res = await fetch(`${window.API_URL}/api/games/active/${activeGameId}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores, keep_stats: keepStats })
      });

      const data = await res.json();

      // Navigate to game over screen
      navigate('/game-over', {
        state: {
          gameData: {
            gameName: activeGame.game_name,
            players: scores.map((s, i) => ({
              ...activeGame.players.find(p => p.user_id === s.user_id),
              score: s.score,
              placement: i + 1
            }))
          },
          sessionId: data.session_id
        }
      });
    } catch (error) {
      console.error('Error ending game:', error);
      alert('Failed to end game');
    }
  };

  const handleQuitGame = () => {
    setShowEndModal(true);
  };

  const handleEditGame = () => {
    // Navigate to game setup page in edit mode
    navigate(`/game-setup/${activeGame.slug}?activeGameId=${activeGameId}&editMode=true`);
  };

  const handleCancelGame = async (keepStats) => {
    try {
      if (!keepStats) {
        await fetch(`${window.API_URL}/api/games/active/${activeGameId}`, {
          method: 'DELETE'
        });
      }
      navigate('/');
    } catch (error) {
      console.error('Error cancelling game:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading game...</p>
      </div>
    );
  }

  if (loadError || !activeGame) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full text-center">
          <p className="text-red-400 text-lg font-bold mb-2">Failed to load game</p>
          <p className="text-slate-400 text-sm mb-4">The server couldn't be reached and there's no copy of this game saved on this device.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setLoading(true); setLoadError(false); fetchActiveGame(); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg font-medium"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render appropriate game component based on game type
  const GameComponent = activeGame.slug === 'five-crowns' ? FiveCrownsGame :
                        activeGame.slug === 'phase-10' ? Phase10Game :
                        null;

  if (!GameComponent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Game type not yet supported: {activeGame.game_name}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {saveError && (
        <div className="fixed top-0 left-0 right-0 bg-amber-600 text-white text-center py-2 px-4 text-sm font-medium z-50 shadow-lg">
          Can't reach the server — scores are saved on this device and will sync
          automatically.{' '}
          {consecutiveSaveFailures > 5 && 'Still trying. Keep playing; nothing is lost.'}
          <button
            onClick={async () => {
              const pending = readPendingState(activeGameId) || activeGame.game_state;
              try {
                await pushState(pending);
                clearPendingState(activeGameId);
                setSaveError(false);
                setConsecutiveSaveFailures(0);
                setServedFromCache(false);
              } catch (e) { /* still failing; the background retry continues */ }
            }}
            className="ml-3 underline"
          >
            Retry now
          </button>
        </div>
      )}
      {servedFromCache && !saveError && (
        <div className="fixed top-0 left-0 right-0 bg-slate-600 text-white text-center py-2 px-4 text-sm font-medium z-50 shadow-lg">
          Showing this game from your device — the server is unreachable.
          <button onClick={() => fetchActiveGame()} className="ml-3 underline">
            Reconnect
          </button>
        </div>
      )}
      <GameComponent
        activeGame={activeGame}
        gameState={activeGame.game_state || {}}
        onUpdateGameState={handleUpdateGameState}
        onEndGame={handleEndGame}
        onQuitGame={handleQuitGame}
        onEditGame={handleEditGame}
        socket={socket}
      />

      {/* End/Cancel Game Modal */}
      {showEndModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-white mb-2">Leave Game?</h3>
            <p className="text-slate-400 text-sm mb-4">
              The game isn't finished yet. What would you like to do?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => setShowEndModal(false)}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-lg font-medium"
              >
                Keep Playing
              </button>
              <button
                onClick={() => handleCancelGame(true)}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white py-2.5 rounded-lg font-medium"
              >
                Save & Exit (Resume Later)
              </button>
              <button
                onClick={() => handleCancelGame(false)}
                className="w-full bg-red-600 hover:bg-red-500 text-white py-2.5 rounded-lg font-medium"
              >
                Delete Game
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
